import { LoaderCircle, Share2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  ExportAction,
  ExportTooLargeError,
  ExportWorkerError,
  ShareOutcome,
  canShareFiles,
  createShareCapabilityFiles,
  download,
  downloadAll,
  downloadArchive,
  formatBytes,
  planExport,
  runExport,
  runExportArchive,
  ResizeMode,
  shareResults,
  type ExportOptions,
} from '@/core/export'
import { firstImageNode, type Document } from '@/core/document'
import {
  addBreadcrumb,
  ReportingEvent,
  ReportingStage,
  surfaceError,
  trackUsage,
  UsageEvent,
} from '@/core/reporting'
import { useEditSession } from '@/core/store/editSession'
import { selectRootNode } from '@/core/store/selectors'
import { Platform, platformForPointer } from '@/lib/platform'
import { markShareHintSeen, useShareHintSeen } from '@/lib/shareHint'
import { TEST_IDS } from '@/lib/testIds'
import { useIsCoarsePointer } from '@/lib/usePointerType'
import { Button } from '@/ui/shadcn/button'

interface ExportButtonProps {
  compact?: boolean
  className?: string
  testId?: string
  action?: ExportAction
  label?: string
  secondary?: boolean
  showHint?: boolean
}

function exactBytes(bytes: number): string {
  return formatBytes(bytes).replace(/^~/, '')
}

function trackSuccessfulExport(
  document: Document,
  options: ExportOptions,
  outputs: number,
  totalBytes: number,
): void {
  addBreadcrumb(ReportingEvent.ExportFinished, {
    format: options.format,
    resizeMode: options.resizeMode,
    outputs,
  })
  trackUsage(UsageEvent.ExportFinished, {
    rootKind: document.root.kind,
    presetId: document.output.presetId,
    splitKind: document.output.split?.kind ?? null,
    format: options.format,
    resizeMode: options.resizeMode,
    outputs,
    totalBytes: Math.round(totalBytes / 1_024) * 1_024,
  })
}

function trackFailedExport(error: unknown): void {
  const errorName = error instanceof Error ? error.name : 'UnknownError'
  addBreadcrumb(ReportingEvent.ExportFailed, { errorName })
  trackUsage(UsageEvent.ExportFailed, { errorName })
}

export function ExportButton({
  compact = false,
  className,
  testId,
  action = ExportAction.Auto,
  label,
  secondary = false,
  showHint = false,
}: ExportButtonProps) {
  const isCoarsePointer = useIsCoarsePointer()
  const platform = platformForPointer(isCoarsePointer, navigator.userAgent)
  const shareHintSeen = useShareHintSeen()
  const isExporting = useEditSession((state) => state.isExporting)
  const document = useEditSession((state) => state.document)
  const sources = useEditSession((state) => state.sources)
  const exportOptions = useEditSession((state) => state.exportOptions)
  if (!document) throw new Error('Export button requires a loaded document')
  const visiblePlan = planExport(document, sources, exportOptions)
  const resolvedAction = action === ExportAction.Auto
    ? canShareFiles(createShareCapabilityFiles(visiblePlan.outputs.length, exportOptions.format))
      ? ExportAction.Share
      : ExportAction.SaveFiles
    : action
  const multipleFiles = visiblePlan.outputs.length > 1
  const resolvedTestId = testId ?? (resolvedAction === ExportAction.Share
    ? TEST_IDS.exportShare
    : multipleFiles ? TEST_IDS.exportDownloadAll : TEST_IDS.exportSave)
  const resolvedLabel = isCoarsePointer && resolvedAction === ExportAction.Share
    ? 'Share'
    : isCoarsePointer && resolvedAction === ExportAction.SaveFiles
      ? multipleFiles
        ? 'Download all'
        : platform === Platform.Ios ? 'Download to Files' : 'Download'
      : label ?? (resolvedAction === ExportAction.Share
        ? 'Share'
        : multipleFiles ? 'Download all' : compact ? 'Save' : 'Save image')
  const hintVisible = showHint
    && resolvedAction === ExportAction.Share
    && platform !== Platform.Desktop
    && !shareHintSeen

  async function handleExport() {
    const state = useEditSession.getState()
    state.setIsExporting(true)
    try {
      if (!state.document) throw new Error('Export requires a loaded document')
      const exportDocument = state.document
      const exportOptions = state.exportOptions
      const root = selectRootNode(state)
      const image = firstImageNode(root)
      const source = state.sources.get(image.sourceId)
      if (!source) throw new Error(`Export source "${image.sourceId}" is missing`)
      const plan = planExport(exportDocument, state.sources, exportOptions)
      addBreadcrumb(ReportingEvent.ExportStarted, {
        format: exportOptions.format,
        resizeMode: exportOptions.resizeMode,
        outputs: plan.outputs.length,
      })
      if (resolvedAction !== ExportAction.Share) {
        trackUsage(UsageEvent.SaveClicked, { files: plan.outputs.length })
      }
      plan.warnings?.forEach((warning) => toast.warning(warning))
      if (resolvedAction === ExportAction.SaveArchive) {
        const archive = await runExportArchive(state.sources, exportDocument, image.sourceId, plan, exportOptions)
        downloadArchive(archive)
        trackSuccessfulExport(exportDocument, exportOptions, plan.outputs.length, archive.blob.size)
        toast.success(`Saved ${archive.fileName} · ${exactBytes(archive.blob.size)}`)
      } else {
        const results = await runExport(state.sources, exportDocument, image.sourceId, plan, exportOptions)
        const first = results[0]
        if (!first) throw new Error('Export completed without a result')
        const totalBytes = results.reduce((total, result) => total + result.blob.size, 0)
        const resultSummary = results.length > 1
          ? `${results.length} files · ${exactBytes(totalBytes)} total`
          : `${first.fileName} · ${exactBytes(first.blob.size)}`
        const deliverDownloads = async () => {
          if (results.length > 1) await downloadAll(results)
          else download(results)
          trackSuccessfulExport(exportDocument, exportOptions, results.length, totalBytes)
          toast.success(`Saved ${resultSummary}`)
        }
        if (resolvedAction === ExportAction.Share) {
          trackUsage(UsageEvent.ShareOpened, { files: results.length })
          let outcome: ShareOutcome
          try {
            outcome = await shareResults(results)
          } catch (shareError) {
            trackFailedExport(shareError)
            surfaceError(shareError, 'Sharing failed with an unknown error', {
              stage: ReportingStage.Application,
              toastOptions: {
                action: {
                  label: 'Download instead',
                  onClick: () => {
                    void deliverDownloads().catch((downloadError: unknown) => {
                      trackFailedExport(downloadError)
                      surfaceError(downloadError, 'Could not download the encoded export.')
                    })
                  },
                },
              },
            })
            return
          }
          if (outcome === ShareOutcome.Cancelled) {
            trackUsage(UsageEvent.ShareCancelled, {})
            return
          }
          markShareHintSeen()
          trackSuccessfulExport(exportDocument, exportOptions, results.length, totalBytes)
          toast.success(`Shared ${resultSummary}`)
        } else {
          await deliverDownloads()
        }
      }
    } catch (error) {
      trackFailedExport(error)
      surfaceError(error, 'Export failed with an unknown error', {
        stage: error instanceof ExportWorkerError ? ReportingStage.ExportWorker : ReportingStage.Application,
        ...(error instanceof ExportTooLargeError ? {
          toastOptions: {
            action: {
              label: 'Export at Instagram size instead',
              onClick: () => {
                useEditSession.getState().setExportOptions({
                  resizeMode: ResizeMode.InstagramTarget,
                  customLongestSide: null,
                })
                void handleExport()
              },
            },
          },
        } : {}),
      })
    } finally {
      useEditSession.getState().setIsExporting(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        className={className}
        variant={secondary ? 'outline' : 'default'}
        disabled={isExporting}
        onClick={() => void handleExport()}
        data-testid={resolvedTestId}
      >
        {isExporting ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
        {!isExporting && resolvedAction === ExportAction.Share ? <Share2 aria-hidden="true" /> : null}
        {isExporting ? 'Exporting…' : resolvedLabel}
      </Button>
      {hintVisible ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid={TEST_IDS.exportShareHint}>
          {platform === Platform.Ios ? (
            <>Pick <strong>Instagram</strong> to post now, or <strong>Save Image</strong> to keep it in Photos.</>
          ) : (
            <>Pick <strong>Instagram</strong> to post now, or your gallery app to save.</>
          )}
        </p>
      ) : null}
    </>
  )
}
